import {
  IUiShellPort,
  EditionInfoDTO,
  PresetSaveRequestDTO,
  PresetItemSummaryDTO,
} from '../ports/types';

const STORAGE_KEY = 'purinpurin_presets_v1';

export class LocalStorageUiShell implements IUiShellPort {
  private editionType: EditionInfoDTO['edition'];

  constructor(editionType: EditionInfoDTO['edition'] = 'web_free') {
    this.editionType = editionType;
    this.initDefaultPresets();
  }

  setEdition(edition: EditionInfoDTO['edition']): void {
    this.editionType = edition;
  }

  getEditionInfo(): EditionInfoDTO {
    return {
      edition: this.editionType,
      maxPresets: this.editionType === 'web_free' ? 0 : -1,
      isAudioEnabled: this.editionType !== 'web_free',
      isAutoWobbleEnabled: this.editionType !== 'web_free',
      isWatermarkForced: this.editionType === 'web_free',
      isUrlImportEnabled: this.editionType !== 'web_free',
    };
  }

  private initDefaultPresets(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (!existing) {
      const defaultPresets: Record<string, PresetSaveRequestDTO> = {
        p_default: {
          name: 'プリン(標準)',
          assetId: 'sample-pudding',
          params: { softness: 0.12, stiffness: 0.15, damping: 0.91 },
        },
        p_soft: {
          name: 'ふわふわマシュマロ',
          assetId: 'sample-pudding',
          params: { softness: 0.22, stiffness: 0.11, damping: 0.94 },
        },
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultPresets));
    }
  }

  private getStore(): Record<string, PresetSaveRequestDTO> {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  }

  private setStore(store: Record<string, PresetSaveRequestDTO>): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  }

  async savePreset(
    dto: PresetSaveRequestDTO
  ): Promise<{ success: boolean; presetId?: string; errorMessage?: string }> {
    const store = this.getStore();
    const info = this.getEditionInfo();

    if (info.maxPresets === 0) {
      return { success: false, errorMessage: 'PRESET_SAVE_LOCKED: プリセット保存機能は有料版限定です。' };
    }

    const newId = 'preset_' + Date.now();
    store[newId] = dto;
    this.setStore(store);
    return { success: true, presetId: newId };
  }

  async listPresets(): Promise<PresetItemSummaryDTO[]> {
    const store = this.getStore();
    return Object.entries(store).map(([id, p]) => ({
      id,
      name: p.name,
      assetId: p.assetId,
      updatedAt: Date.now(),
    }));
  }

  async loadPreset(presetId: string): Promise<PresetSaveRequestDTO | null> {
    const store = this.getStore();
    return store[presetId] || null;
  }

  async deletePreset(presetId: string): Promise<boolean> {
    if (presetId === 'p_default') return false; // デフォルトは削除不可
    const store = this.getStore();
    if (store[presetId]) {
      delete store[presetId];
      this.setStore(store);
      return true;
    }
    return false;
  }
}
